/**
 * Copyright 2021 mmmint.ai info@mmmint.ai - All Rights Reserved.
 * Unauthorized copying of this file, via any medium is strictly prohibited.
 * Proprietary and confidential to MMM Intelligence UG (haftungsbeschränkt).
 */

import { ILocalDataAccessLayer } from "./local-data-access-layer.interface";

/**
 * Class for data access
 */
export abstract class AbstractLocalDataAccessLayer<T> implements ILocalDataAccessLayer<T> {
  /**
   * @inheritdoc
   */
  private _entities: T[] = [];

  /**
   * A local map of the position of entities.
   */
  private _map = new Map<string, number>();

  /**
   * @inheritdoc
   */
  abstract getIdentifier(entity: T): string;

  /**
   * Maps an entity to a new entity.
   * Can be used to extend the entity with additional properties, e.g. "billing.entity" adds additional getters and fetch methods
   * It can be overwritten by the concrete data access layer, default behavior does not change the entity.
   *
   * @param entity
   */
  protected mapEntity(entity: T): T {
    return entity;
  }

  /**
   * @inheritdoc
   */
  get(id: string): T | undefined {
    const pos = this._map.get(id);
    if (pos === undefined) {
      return undefined;
    }

    const mappedEntity = this.mapEntity(this._entities[pos]);
    this._entities.splice(pos, 1, mappedEntity);

    return this._entities[pos];
  }

  /**
   * @inheritdoc
   */
  set(entity: T): T {
    const id = this.getIdentifier(entity);
    let pos = this._map.get(id);

    if (pos === undefined) {
      const mappedEntity = this.mapEntity(entity);
      this._entities.push(mappedEntity);
      this._map.set(id, this._entities.length - 1);
      pos = this._entities.length - 1;
    } else {
      const mappedEntity = this.mapEntity({ ...this.entities[pos], ...entity });
      this.entities.splice(pos, 1, mappedEntity);
    }

    return this.entities[pos];
  }

  /**
   * @inheritdoc
   */
  delete(entity: T): T {
    const id = this.getIdentifier(entity);

    const indexOfDeleted = this._map.get(id);
    if (indexOfDeleted !== undefined) {
      this.entities.splice(indexOfDeleted, 1);
      this._map.delete(id);
    }

    return this.mapEntity(entity);
  }

  /**
   * @inheritdoc
   */
  clear() {
    this._map.clear();
    this._entities.splice(0);
  }

  /**
   * @inheritdoc
   */
  get entities() {
    this._entities.forEach((e, index) => this._entities.splice(index, 1, this.mapEntity(e)));

    return this._entities;
  }

  /**
   * @inheritdoc
   */
  set entities(entities: T[]) {
    this._entities = entities;

    this._map = new Map(this._entities.map((entity, index) => [this.getIdentifier(entity), index]));
  }
}
